import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseProfile, BuyerProfile } from '@/hooks/useProfile';
import { authService } from '../services/authService';
import { useProfileSingleton, clearProfileCache } from '@/hooks/useProfileSingleton';
import { tokenService } from '../services/tokenService';
import { API_BASE_URL } from '@/config/api';
import { UserRoles, LoginResponse, AuthState } from '../types/auth';
import { ApiError } from '../utils/errorHandler';
import { clearReturnTo, consumeReturnTo, peekReturnTo } from '@/utils/returnTo';
import { clearReferralCode } from '@/utils/referralCode';
import cacheManager from '@/utils/cacheManager';

interface AuthContextType extends AuthState {
  // Auth actions
  loginPhone: (phone: string, otp: string) => Promise<void>;
  loginEmail: (email:string, password:string)=>Promise<void>;
  logout: () => void;
  requestOtp: (phoneNumber: string) => Promise<void>; // signup OTP
  requestLoginOtp: (phoneNumber: string) => Promise<void>;
  completeSignup: (data: {
    phoneNumber: string;
    otp: string;
    firstName: string;
    lastName: string;
    email?: string;
  }) => Promise<void>;
  
  // Token management
  refreshToken: () => Promise<void>;
  
  // Utilities
  isTokenExpired: () => boolean;
  getRemainingTokenTime: () => number;
  refreshUser: () => Promise<void>;

  // Full profile cache (base + role-specific)
  profileState: {
    loading: boolean;
    error: string | null;
    base: BaseProfile | null;
    buyer: BuyerProfile | null;
    profileCompletion?: { isComplete: boolean; missingFields: string[] } | null;
  };
  refreshProfile: () => Promise<void>;

  // Signup UI state
  signupState: {
    screen: 'signin' | 'signup' | 'phone-otp' | 'email-otp' | 'dual-otp' | 'forgot-password';
    method: 'phone' | 'email';
    phone?: string;
    expiresAt?: number;
  };
  setSignupState: React.Dispatch<React.SetStateAction<{
    screen: 'signin' | 'signup' | 'phone-otp' | 'email-otp' | 'dual-otp' | 'forgot-password';
    method: 'phone' | 'email';
    phone?: string;
    expiresAt?: number;
  }>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const finishAuthRedirect = (
  navigate?: (to: string, options?: { replace?: boolean }) => void,
  options?: { profileComplete?: boolean | null; replace?: boolean; consumeReturnTo?: boolean }
): string => {
  const returnTo = peekReturnTo();
  const hasExplicitProfileComplete = options && Object.prototype.hasOwnProperty.call(options, 'profileComplete');
  let needsProfileCompletion = options?.profileComplete === false;
  if (!hasExplicitProfileComplete) {
    try {
      const cached = JSON.parse(localStorage.getItem('fb_profile_cache') || 'null');
      if (cached?.profileCompletion && cached.profileCompletion.isComplete === false) {
        needsProfileCompletion = true;
      }
    } catch {}
  }

  const target = needsProfileCompletion ? '/profile-completion' : returnTo || '/dashboard';
  if (
    !needsProfileCompletion &&
    !returnTo &&
    typeof window !== 'undefined' &&
    !window.location.pathname.startsWith('/auth')
  ) {
    return target;
  }
  if (target === '/dashboard' && typeof window !== 'undefined') {
    console.trace('[dashboard-redirect] finishAuthRedirect', {
      pathname: window.location.pathname,
      returnTo: peekReturnTo(),
      profileCompletion: (() => {
        try { return JSON.parse(localStorage.getItem('fb_profile_cache') || 'null')?.profileCompletion ?? null; } catch { return null; }
      })()
    });
  }
  if (navigate) {
    navigate(target, { replace: options?.replace ?? true });
  }
  // Consume only when navigation happens, unless explicitly opted in.
  if (
    !needsProfileCompletion &&
    returnTo &&
    (navigate || options?.consumeReturnTo === true)
  ) {
    consumeReturnTo();
  }
  return target;
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    user: null,
    currentRole: null,
    availableRoles: [],
    token: null,
    loading: true,
    error: null,
  });

  // Cached full profile (base + buyer role data)
  const [profileState, setProfileState] = useState<{
    loading: boolean;
    error: string | null;
    base: BaseProfile | null;
    buyer: BuyerProfile | null;
    profileCompletion?: { isComplete: boolean; missingFields: string[] } | null;
  }>({ loading: true, error: null, base: null, buyer: null });

  // Signup phase state to drive UI (form | otp)
  const [signupState, setSignupState] = useState<{
    screen: 'signin' | 'signup' | 'phone-otp' | 'email-otp' | 'dual-otp' | 'forgot-password';
    method: 'phone' | 'email';
    phone?: string;
    expiresAt?: number; // timestamp ms
  }>({ screen: 'signin', method: 'phone' });

  // Check for existing auth on mount
  useEffect(() => {
    checkExistingAuth();
  }, []);

  // Auto token refresh
  useEffect(() => {
    if (authState.isAuthenticated) {
      tokenService.startAutoRefresh(
        (newToken) => {
          setAuthState(prev => ({ ...prev, token: newToken }));
        },
        () => {
          console.warn('Token auto-refresh failed; keeping session active until refresh endpoint is available.');
          tokenService.stopAutoRefresh();
        }
      );

      return () => tokenService.stopAutoRefresh();
    }
  }, [authState.isAuthenticated]);

  /**
   * Check for existing authentication on app load
   */
  const checkExistingAuth = async () => {
    try {
      const token = authService.getStoredAuthToken();
      const role = authService.getCurrentUserRole();

      if (!token) {
        setAuthState(prev => ({ ...prev, loading: false }));
        return;
      }

      // Validate token is not expired
      if (tokenService.isTokenExpired(token)) {
        await logout();
        return;
      }

      // Validate role is fabric-buyer
      if (role !== UserRoles.FabricBuyer) {
        await logout();
        setAuthState(prev => ({ 
          ...prev, 
          loading: false,
          error: 'This app is exclusively for fabric buyers.'
        }));
        return;
      }

      // Try to restore stored name (from previous signup/login)
      const storedName = JSON.parse(localStorage.getItem('fb_user_name') || '{}');

      // Parse verification flags from token
      const tokenInfo = tokenService.parseToken(token || '') as any;
      const emailVerified = tokenInfo?.emailVerified ?? false;
      const phoneVerified = tokenInfo?.phoneVerified ?? false;
      const authProvider = tokenInfo?.authProvider;

      setAuthState({
        isAuthenticated: true,
        user: {
          _id: 'current-user',
          firstName: storedName.firstName || '',
          lastName: storedName.lastName || '',
          email: storedName.email || '',
          role: [UserRoles.FabricBuyer],
          currentRole: UserRoles.FabricBuyer,
          emailVerified,
          phoneVerified,
          authProvider,
        },
        currentRole: UserRoles.FabricBuyer,
        availableRoles: [UserRoles.FabricBuyer],
        token,
        loading: false,
        error: null,
      });

    } catch (error) {
      console.error('Auth check failed:', error);
      await logout();
    }
  };

  /**
   * Request OTP for phone signup
   */
  const requestOtp = async (phoneNumber: string): Promise<void> => {
    try {
      // No global loading toggle; UI handles local spinner
      
      await authService.requestPhoneSignupOtp({
        phoneNumber,
        platform: 'fabrics',
        role: UserRoles.FabricBuyer
      });

      // Move to OTP phase and set 60s expiry for resend
      const expiresAt = Date.now() + 60 * 1000;
      setSignupState({ ...signupState, screen: 'phone-otp', phone: phoneNumber, expiresAt });

    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.getUserMessage() : 'Failed to request OTP';
      setAuthState(prev => ({ 
        ...prev, 
        error: errorMessage 
      }));
      throw error;
    }
  };

  /**
   * Request OTP for phone login flow
   */
  const requestLoginOtp = async (phoneNumber: string): Promise<void> => {
    try {
      await authService.requestPhoneLoginOtp({
        phoneNumber,
        platform: 'fabrics',
        role: UserRoles.FabricBuyer
      });

      // Move to OTP phase similar to signup
      const expiresAt = Date.now() + 60 * 1000;
      setSignupState({ screen: 'phone-otp', method: 'phone', phone: phoneNumber, expiresAt });
    } catch (error) {
      // propagate to caller for UI toasts
      throw error;
    }
  };

  /**
   * Complete phone signup with OTP
   */
  const completeSignup = async (data: {
    phoneNumber: string;
    otp: string;
    firstName: string;
    lastName: string;
    email?: string;
  }): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      const response = await authService.completePhoneSignup({
        ...data,
        platform: 'fabrics',
        role: UserRoles.FabricBuyer
      });
      const tokenInfo = tokenService.parseToken(response.token || '') as any;
      const authProvider = tokenInfo?.authProvider;

      // Update auth state with user data
      const names = {
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        email: response.user.email
      };
      localStorage.setItem('fb_user_name', JSON.stringify(names));

      setAuthState({
        isAuthenticated: true,
        user: {
          _id: response.user._id || '',
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          email: response.user.email,
          role: [UserRoles.FabricBuyer],
          currentRole: UserRoles.FabricBuyer,
          authProvider,
        },
        currentRole: UserRoles.FabricBuyer,
        availableRoles: [UserRoles.FabricBuyer],
        token: response.token,
        loading: false,
        error: null,
      });

    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.getUserMessage() : 'Signup failed';
      setAuthState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage 
      }));
      throw error;
    }
  };

  /**
   * Verify OTP & complete signup
   */
  const verifyOtp = async (data: {
    phoneNumber: string;
    otp: string;
    firstName: string;
    lastName: string;
    email?: string;
  }): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, error: null }));

      const response = await authService.completePhoneSignup({
        ...data,
        platform: 'fabrics',
        role: UserRoles.FabricBuyer
      });
      const tokenInfo = tokenService.parseToken(response.token || '') as any;
      const authProvider = tokenInfo?.authProvider;

      const names2 = {
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        email: response.user.email
      };
      localStorage.setItem('fb_user_name', JSON.stringify(names2));

      setAuthState({
        isAuthenticated: true,
        user: {
          _id: response.user._id || '',
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          email: response.user.email,
          role: [UserRoles.FabricBuyer],
          currentRole: UserRoles.FabricBuyer,
          authProvider,
        },
        currentRole: UserRoles.FabricBuyer,
        availableRoles: [UserRoles.FabricBuyer],
        token: response.token,
        loading: false,
        error: null,
      });

      // Refresh profile cache after signup OTP verification
      await refreshProfileSingleton();

      // Centralised post-auth redirect
      finishAuthRedirect(navigate);

      // Reset signup state
      setSignupState({ screen: 'signin', method: 'phone' });

    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.getUserMessage() : 'Signup failed';
      setAuthState(prev => ({ ...prev, error:errorMessage }));
      throw error;
    }
  };

  /**
   * Login with email + password
   */
  const loginEmail = async (email: string, password: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      const response = await authService.loginWithEmailPassword(email, password);
      const tokenInfo = tokenService.parseToken(response.token || '') as any;
      const authProvider = tokenInfo?.authProvider;

      // Clear any cached profile from a previous session
      localStorage.removeItem('fb_profile_cache');
      clearProfileCache();

      const baseNames = {
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        email: response.user.email,
      };
      localStorage.setItem('fb_user_name', JSON.stringify(baseNames));

      setAuthState({
        isAuthenticated: true,
        user: { ...response.user, authProvider },
        currentRole: response.currentRole as any,
        availableRoles: response.availableRoles as any,
        token: response.token,
        loading: false,
        error: null,
      });

      // Refresh profile cache after successful email login
      await refreshProfileSingleton();

      // Centralised post-auth redirect
      finishAuthRedirect(navigate);
    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.getUserMessage() : 'Login failed';
      setAuthState(prev => ({ ...prev, loading: false, error: errorMessage }));
      throw error;
    }
  };

  /**
   * Login with phone and OTP
   */
  const loginPhone = async (phoneNumber: string, otp: string): Promise<void> => {
    try {
      setAuthState(prev => ({ ...prev, loading: true, error: null }));

      // Clear any cached profile from previous session
      localStorage.removeItem('fb_profile_cache');
      clearProfileCache();

      const response = await authService.loginWithPhone({
        phoneNumber,
        otp,
        platform: 'fabrics',
        role: UserRoles.FabricBuyer
      });
      const tokenInfo = tokenService.parseToken(response.token || '') as any;
      const authProvider = tokenInfo?.authProvider;

      const names3 = {
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        email: response.user.email
      };
      localStorage.setItem('fb_user_name', JSON.stringify(names3));

      setAuthState({
        isAuthenticated: true,
        user: {
          _id: response.user._id || '',
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          email: response.user.email,
          role: [UserRoles.FabricBuyer],
          currentRole: UserRoles.FabricBuyer,
          authProvider,
        },
        currentRole: UserRoles.FabricBuyer,
        availableRoles: [UserRoles.FabricBuyer],
        token: response.token,
        loading: false,
        error: null,
      });

      // Refresh profile cache after successful phone login (best-effort).
      try {
        await refreshProfileSingleton();
      } catch {}

      // Centralised post-auth redirect
      finishAuthRedirect(navigate);

    } catch (error) {
      const errorMessage = error instanceof ApiError ? error.getUserMessage() : 'Login failed';
      setAuthState(prev => ({ 
        ...prev, 
        loading: false, 
        error: errorMessage 
      }));
      throw error;
    }
  };

  /**
   * Logout user and clear all auth data
   */
  const logout = async (): Promise<void> => {
    authService.logout();
    clearReturnTo();
    clearReferralCode();
    cacheManager.clearSnapshot('buyer:requests:list');
    cacheManager.clearSnapshot('buyer:orders:list');
    cacheManager.clearSnapshot('buyer:bids:list');
    cacheManager.clearSnapshot('dashboard:metrics');
    cacheManager.clearSnapshot('dashboard:recentRequests');
    setAuthState({
      isAuthenticated: false,
      user: null,
      currentRole: null,
      availableRoles: [],
      token: null,
      loading: false,
      error: null,
    });
    // Reset signup UI state to initial
    setSignupState({ screen: 'signin', method: 'phone' });
    // Clear all cached profile/user info
    localStorage.removeItem('fb_user_name');
    localStorage.removeItem('fb_profile_cache');
    clearProfileCache();
    // SPA redirect to auth page without full reload to avoid flash
    window.history.pushState({}, '', '/auth');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  /**
   * Refresh auth token
   */
  const refreshToken = async (): Promise<void> => {
    try {
      const newToken = await tokenService.refreshToken();
      setAuthState(prev => ({ ...prev, token: newToken }));
    } catch (error) {
      console.error('Token refresh failed:', error);
      await logout();
    }
  };

  /**
   * Check if current token is expired
   */
  const isTokenExpired = (): boolean => {
    const token = authService.getStoredAuthToken();
    return tokenService.isTokenExpired(token || '');
  };

  /**
   * Get remaining token time in milliseconds
   */
  const getRemainingTokenTime = (): number => {
    const token = authService.getStoredAuthToken();
    return tokenService.getRemainingTime(token || '');
  };

  const {
      loading: singletonLoading,
      error: singletonError,
      base: singletonBase,
      buyer: singletonBuyer,
      profileCompletion: singletonCompletion,
      refreshProfile: refreshProfileSingleton,
    } = useProfileSingleton();

    // Mirror singleton values into legacy state so the rest of AuthContext stays unchanged
    useEffect(() => {
      setProfileState({
        loading: singletonLoading,
        error: singletonError,
        base: singletonBase,
        buyer: singletonBuyer,
        profileCompletion: singletonCompletion,
      });
    }, [singletonLoading, singletonError, singletonBase, singletonBuyer, singletonCompletion]);

    const refreshProfile = async () => {
      await refreshProfileSingleton();
    };

  // Kick off profile load once authenticated
  useEffect(() => {
    if (authState.isAuthenticated) {
      // Attempt to show cached data instantly
      try {
        const cached = JSON.parse(localStorage.getItem('fb_profile_cache') || 'null');
        if (cached?.base) {
          setProfileState({ loading: true, error: null, ...cached });
        }
      } catch {}

    }
  }, [authState.isAuthenticated]);

  const contextValue: AuthContextType = {
    ...authState,
    signupState,
    setSignupState,
    loginPhone,
    loginEmail,
    logout,
    requestOtp, // signup OTP
    requestLoginOtp,
    completeSignup: verifyOtp,
    refreshToken,
    isTokenExpired,
    getRemainingTokenTime,
    refreshUser: refreshProfile,
    profileState,
    refreshProfile,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Hook to use auth context
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Hook to check if user is authenticated fabric buyer
 */
export const useAuthGuard = () => {
  const { isAuthenticated, currentRole, loading } = useAuth();
  
  const isAuthorizedFabricBuyer = isAuthenticated && currentRole === UserRoles.FabricBuyer;
  const shouldRedirectToAuth = !loading && !isAuthorizedFabricBuyer;
  
  return {
    isAuthorizedFabricBuyer,
    shouldRedirectToAuth,
    loading
  };
}; 
